/**
 * App component - Root application component with authentication and layout
 *
 * This component sets up:
 * - Okta authentication and security context
 * - Global toast notifications
 * - Application layout and maintenance banner
 * - Lock-aware fetch hook for network requests
 *
 * The component configures Okta with OpenID Connect using environment variables
 * for issuer and client ID, and handles OAuth callback routing.
 *
 * @component
 * @returns {JSX.Element} The root application element wrapped with authentication and layout
 */

import { useNavigate, Outlet } from 'react-router-dom'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { Security } from '@okta/okta-react'
import { ToastContainer, Slide } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { Layout } from './Layout'
import AppMaintenanceBanner from './AppMaintenanceBanner'
import useLockAwareFetch from './common/hooks/useLockAwareFetch'
import RealUserInfoProvider from './common/RealUserInfoContext'

function App() {
  const navigate = useNavigate()

  const lockAwareFetch = useLockAwareFetch({ timeOut: 10_000, lockHeuristicWindow: 90_000 })

  const config = {
    redirectUri: `${window.location.origin}/okta/callback`,
    scopes: ['openid', 'profile', 'email'],
    issuer: window.env.VITE_ISSUER,
    clientId: window.env.VITE_CLIENT_ID,
    tokenManager: {
      autoRenew: true,
    },
  }

  const oktaAuth = new OktaAuth(config)

  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin))
  }

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Layout>
        <ToastContainer
          position='top-left'
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme='dark'
          transition={Slide}
        />
        <RealUserInfoProvider lockAwareFetch={lockAwareFetch}>
          <AppMaintenanceBanner lockAwareFetch={lockAwareFetch}>
            <Outlet context={{ lockAwareFetch }} />
          </AppMaintenanceBanner>
        </RealUserInfoProvider>
      </Layout>
    </Security>
  )
}

export default App
